import { Money, type Currency } from '@principle-theorem/accounting';
import {
  AccountCreditType,
  IAccountCredit,
  IAppointment,
  IInteractionV2,
  IInvoice,
  InvoiceStatus,
  InvoiceType,
  IPatient,
  IPractice,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  doc,
  DocumentReference,
  FieldValue,
  Firestore,
  type Transaction as FirebaseTransaction,
  type Timestamp,
} from '@principle-theorem/shared';
import { AccountCredit } from '../account-credit/account-credit';
import { createInvoice } from '../invoice/create-invoice';
import { depositToLineItem } from '../invoice/custom-line-items';
import { Invoice } from '../invoice/invoice';
import { Patient } from '../patient/patient';
import { Transaction } from '../transaction/transaction';

export interface IDepositInvoiceReferences {
  accountCreditRef: DocumentReference<IAccountCredit>;
  interactionRef: DocumentReference<IInteractionV2>;
  invoiceRef: DocumentReference<IInvoice>;
}

/**
 * Provenance for a deposit paid online. The reference is required because a
 * Stripe payment that cannot be traced back to its PaymentIntent can never be
 * reconciled against the provider.
 */
export interface IStripeDepositPayment {
  provider: TransactionProvider.Stripe;
  reference: string;
}

/**
 * Provenance for a deposit recorded by staff. There is no provider reference to
 * carry, so the ledger falls back to the invoice's own reference.
 */
export interface IManualDepositPayment {
  provider: TransactionProvider.Manual;
  reference?: string;
}

/**
 * How the deposit money actually moved. This is stamped onto the payment
 * transaction so the ledger records real provenance rather than a guess.
 */
export type DepositPaymentProvenance =
  | IStripeDepositPayment
  | IManualDepositPayment;

export interface ICreateDepositInvoiceParams {
  patientRef: DocumentReference<IPatient>;
  practiceRef: DocumentReference<IPractice>;
  /**
   * Cents in, converted to dollars at the line-item/credit boundary. This is the
   * input boundary from Stripe, which reports every amount in the currency's
   * minor unit.
   */
  amountCents: number;
  currency: Currency;
  paidAt: Timestamp;
  payment: DepositPaymentProvenance;
  description?: string;
  appointmentRef?: DocumentReference<IAppointment>;
  idempotencyKey?: string;
  references?: IDepositInvoiceReferences;
}

/**
 * Owns the paid-deposit invoice: the invoice, its interaction and the account
 * credit that lets the deposit be spent later. All three are written in one
 * transaction because a deposit that is charged but not credited is money the
 * patient cannot see.
 */
export class DepositInvoice {
  /**
   * Creates the paid invoice, payment transaction, and account credit atomically.
   * This deliberately bypasses `AccountingFunctions.addTransactionToInvoice` so
   * the ledger entry and spendable credit commit together. Consequently it skips
   * that flow's status-change interaction, due-credit upsert (this method creates
   * the credit and due-credit discovery deduplicates by `depositUid`), and claim
   * handling (deposit invoices have no claims).
   *
   * Appointment-linked calls are guarded by the transactional appointment read
   * and `deposit.invoiceRef`; deterministic references and the fixed transaction
   * id add replay protection. Appointment-less calls are replay-safe only when an
   * `idempotencyKey` is supplied.
   */
  static async create(
    params: ICreateDepositInvoiceParams
  ): Promise<DocumentReference<IInvoice>> {
    const {
      patientRef,
      practiceRef,
      amountCents,
      currency,
      paidAt,
      payment,
      description,
      appointmentRef,
      idempotencyKey,
      references,
    } = params;

    const { invoiceRef, interactionRef, accountCreditRef } =
      DepositInvoice._resolveReferences(patientRef, idempotencyKey, references);

    return Firestore.runTransaction(
      async (transaction: FirebaseTransaction) => {
        const [patientDocument, practice, appointment] = await Promise.all([
          Firestore.getDoc(patientRef, transaction),
          Firestore.getDoc(practiceRef, transaction),
          appointmentRef
            ? Firestore.getDoc(appointmentRef, transaction)
            : Promise.resolve(undefined),
        ]);
        if (appointment?.deposit?.invoiceRef) {
          return appointment.deposit.invoiceRef;
        }
        const lineItem = depositToLineItem(
          description || 'Deposit',
          Money.fromCents(amountCents),
          appointment?.practitioner,
          undefined,
          undefined,
          appointmentRef
        );
        const invoice = await createInvoice(
          patientDocument,
          practice,
          undefined,
          [lineItem],
          InvoiceType.Invoice,
          {
            transaction,
            invoiceRef,
            interactionRef,
            invoiceOverrides: { paidAt },
            interactionOverrides: { practiceRef },
          }
        );

        const paymentTransaction = Transaction.init({
          provider: payment.provider,
          reference: payment.reference ?? invoice.reference,
          type: TransactionType.Incoming,
          status: TransactionStatus.Complete,
          amount: Money.fromCents(amountCents),
          from: invoice.to.name,
          to: invoice.from.name,
          practiceRef,
          description,
          createdAt: paidAt,
        });
        await Firestore.saveDoc(
          {
            ...paymentTransaction,
            ref: doc(
              Invoice.transactionCol(asReffable(invoiceRef)),
              'deposit-payment'
            ),
          },
          undefined,
          transaction
        );

        Invoice.updateStatus(invoice, InvoiceStatus.Issued, paidAt);
        Invoice.updateStatus(invoice, InvoiceStatus.Paid, paidAt);
        invoice.issuedAt = paidAt;
        invoice.paidAt = paidAt;
        await Firestore.patchDoc(
          invoice.ref,
          {
            status: InvoiceStatus.Paid,
            statusHistory: invoice.statusHistory,
            issuedAt: invoice.issuedAt,
            paidAt: invoice.paidAt,
          },
          transaction
        );

        const reservedFor = appointmentRef
          ? {
              appointmentRef,
              ...(appointment?.practitioner
                ? { practitioner: appointment.practitioner }
                : {}),
            }
          : {};

        const accountCredit: IAccountCredit = AccountCredit.init({
          description: description || 'Deposit',
          amount: Money.fromCents(amountCents),
          currency,
          type: AccountCreditType.Deposit,
          invoice: invoice.ref,
          depositUid: lineItem.uid,
          reservedFor,
          practiceRef,
          createdAt: paidAt,
        });
        await Firestore.saveDoc(
          { ...accountCredit, ref: accountCreditRef },
          undefined,
          transaction
        );
        if (appointmentRef) {
          await Firestore.patchDoc(
            appointmentRef,
            {
              deposit: { ...appointment?.deposit, invoiceRef: invoice.ref },
              depositInvoiceCreationClaimedAt: FieldValue.delete(),
            },
            transaction
          );
        }

        return invoice.ref;
      }
    );
  }

  private static _resolveReferences(
    patientRef: DocumentReference<IPatient>,
    idempotencyKey?: string,
    references?: IDepositInvoiceReferences
  ): IDepositInvoiceReferences {
    const patient = asReffable(patientRef);
    const deterministicDocumentId = idempotencyKey
      ? `deposit-${idempotencyKey}`
      : undefined;
    const invoiceRef =
      references?.invoiceRef ??
      (deterministicDocumentId
        ? doc(Patient.invoiceCol(patient), deterministicDocumentId)
        : doc(Patient.invoiceCol(patient)));
    const interactionRef =
      references?.interactionRef ??
      (deterministicDocumentId
        ? doc(Invoice.interactionCol(asReffable(invoiceRef)), 'deposit-created')
        : doc(Invoice.interactionCol(asReffable(invoiceRef))));
    const accountCreditRef =
      references?.accountCreditRef ??
      (deterministicDocumentId
        ? doc(AccountCredit.col(patient), deterministicDocumentId)
        : doc(AccountCredit.col(patient)));
    return { invoiceRef, interactionRef, accountCreditRef };
  }
}
